'use client'

import React, { createContext, useCallback, useContext, useMemo } from 'react'
import { useLocaleStore, type Locale } from '@/stores/locale.store'
import { getMessages } from './messages'

type Messages = Record<string, unknown>

function getNested(obj: Messages, path: string): string | undefined {
  const keys = path.split('.')
  let current: unknown = obj
  for (const key of keys) {
    if (current == null || typeof current !== 'object') return undefined
    current = (current as Record<string, unknown>)[key]
  }
  return typeof current === 'string' ? current : undefined
}

function interpolate(str: string, values?: Record<string, string | number>): string {
  if (!values) return str
  return Object.entries(values).reduce(
    (acc, [k, v]) => acc.replace(new RegExp(`\\{${k}\\}`, 'g'), String(v)),
    str
  )
}

export type TFunction = (key: string, values?: Record<string, string | number>) => string

interface I18nContextValue {
  locale: Locale
  setLocale: (locale: Locale) => void
  t: TFunction
  messages: Messages
}

const I18nContext = createContext<I18nContextValue | null>(null)

export function useI18n(namespace?: string): I18nContextValue & { t: TFunction } {
  const ctx = useContext(I18nContext)
  if (!ctx) {
    return {
      locale: 'ko',
      setLocale: () => {},
      messages: {},
      t: (key: string, values?: Record<string, string | number>) => {
        const path = namespace ? `${namespace}.${key}` : key
        const msg = getNested(getMessages('ko') as Messages, path)
        return msg ? interpolate(msg, values) : path
      },
    }
  }
  const t: TFunction = useCallback(
    (key: string, values?: Record<string, string | number>) => {
      const path = namespace ? `${namespace}.${key}` : key
      const msg = getNested(ctx.messages as Messages, path)
      return msg ? interpolate(msg, values) : path
    },
    [ctx.messages, namespace]
  )
  return { ...ctx, t }
}

interface LocaleProviderProps {
  children: React.ReactNode
}

export function LocaleProvider({ children }: LocaleProviderProps) {
  const { locale, setLocale } = useLocaleStore()
  const messages = useMemo(() => getMessages(locale), [locale])

  const value = useMemo<I18nContextValue>(
    () => ({
      locale,
      setLocale,
      t: (key: string, values?: Record<string, string | number>) => {
        const msg = getNested(messages as Messages, key)
        return msg ? interpolate(msg, values) : key
      },
      messages: messages as Messages,
    }),
    [locale, setLocale, messages]
  )

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>
}
